import { type UseQueryResult } from '@tanstack/react-query';
import { type UnitPerformanceInsightsResponse } from '../../../api/customEndpoints/fetchUnitPerformanceScores';
import { CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import { buttonVariants } from '../../../components/ui/button';
import { Pencil } from 'lucide-react';
import { LinkBase, useTranslate } from 'ra-core';
import { Loading } from '../../../components/admin/loading';
import { cn } from '../../../lib/utils';

export function UnitPerformanceInsightsCard({
  unitInsights,
  unitId,
}: {
  unitInsights: UseQueryResult<UnitPerformanceInsightsResponse>;
  unitId: string;
}) {
  const translate = useTranslate();

  return (
    <CardContent>
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                {translate('moonstar.unitPerformance.insights.title')}
              </h2>
              {unitInsights.data ? (
                <Badge
                  variant="secondary"
                  className={cn(
                    unitInsights.data.insights.status === 'no-data'
                      ? 'bg-muted text-muted-foreground'
                      : unitInsights.data.insights.status === 'draft'
                        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                        : 'bg-green-600/10 text-green-700 dark:text-green-400'
                  )}
                >
                  {translate(
                    unitInsights.data.insights.status === 'no-data'
                      ? 'moonstar.unitPerformance.insights.status.noData'
                      : unitInsights.data.insights.status === 'draft'
                        ? 'moonstar.unitPerformance.insights.status.draft'
                        : 'moonstar.unitPerformance.insights.status.published'
                  )}
                </Badge>
              ) : null}
            </div>
            <LinkBase
              to={`/units/${unitId}/performance/insights`}
              className={buttonVariants({ variant: 'outline' })}
            >
              <Pencil />
              {translate('ra.action.edit', { _: 'Edit' })}
            </LinkBase>
          </div>
          {unitInsights.isPending ? (
            <Loading />
          ) : unitInsights.isError ? (
            <p className="text-destructive">
              {translate('moonstar.unitPerformance.insights.loadError', {
                error: String(unitInsights.error),
              })}
            </p>
          ) : !unitInsights.data.insights.content ? (
            <p className="text-sm text-muted-foreground">
              {translate('moonstar.unitPerformance.insights.empty')}
            </p>
          ) : (
            <p className="line-clamp-3 whitespace-pre-wrap text-sm">
              {unitInsights.data.insights.content}
            </p>
          )}
        </div>

        {unitInsights.data?.previousInsights.length ? (
          <div className="space-y-2">
            <h3 className="font-medium">
              {translate('moonstar.unitPerformance.insights.recent')}
            </h3>
            <div>
              {unitInsights.data.previousInsights.map((insight) => (
                <PreviousInsightDisplay
                  key={insight.id}
                  date={insight.publishedOn ?? insight.createdOn}
                  details={insight.content ?? ''}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </CardContent>
  );
}

export function PreviousInsightDisplay(props: {
  date: string;
  details: string;
}) {
  return (
    <Accordion multiple>
      <AccordionItem value={props.date}>
        <AccordionTrigger className="py-2">
          {new Date(props.date).toLocaleDateString(undefined, {
            dateStyle: 'short',
          })}
        </AccordionTrigger>
        <AccordionContent>
          <p className="whitespace-pre-wrap text-sm">{props.details}</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
